import {
    Box,
    Button,
    Card,
    Chip,
    Dialog,
    DialogContent,
    IconButton,
    Menu,
    MenuItem,
    Skeleton,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    TextField,
    Tooltip,
    Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import FileUploadOutlinedIcon from "@mui/icons-material/FileUploadOutlined";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import LinkOutlinedIcon from "@mui/icons-material/LinkOutlined";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import WarningAmberOutlinedIcon from "@mui/icons-material/WarningAmberOutlined";
import LinkOffOutlinedIcon from "@mui/icons-material/LinkOffOutlined";
import CloseOutlinedIcon from "@mui/icons-material/CloseOutlined";
import CheckOutlinedIcon from "@mui/icons-material/CheckOutlined";
import {useState} from "react";
import {Trash2} from "react-feather";

const buildDraftRowId = (prefix, idx) => {
  const rand =
    typeof crypto !== "undefined" && crypto?.randomUUID
      ? crypto.randomUUID()
      : Math.random().toString(16).slice(2);
  return `${prefix}-${idx}-${Date.now()}-${rand}`;
};

const modes = [
  {
    title: "Bank-Level Security",
    description: "All data is secured with 256-bit SSL encryption.",
    icon: "/Padlock Shield Streamline UX Duotone.svg",
  },
  {
    title: "Read-only Access",
    description: "We can see your info, but can’t move money.",
    icon: "/View Eye Streamline UX Duotone.svg",
  },
  {
    title: "Credential Security",
    description: "Plaid securely handles your login.",
    icon: "/Password Secure Streamline UX Duotone.svg",
  },
  {
    title: "HIPAA Compliant",
    description: "We meet all healthcare data standards.",
    icon: "/Like Sparkling 1 Streamline UX Duotone.svg",
  },
];

export const BankLinkInfo = ({ fontFam = "Rethink Sans" } = {}) => {
  return (
    <Box
      sx={{
        width: "100%",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        gap: "32px",
      }}
    >
      <Box sx={{ width: "100%" }}>
        <Typography
          sx={{
            fontFamily: fontFam,
            fontWeight: 600,
            fontSize: "1.5rem",
            lineHeight: "133%",
            textAlign: "center",
            mb: 1,
          }}
        >
          Connect Your Bank Accounts
        </Typography>
        <Typography
          sx={{
            fontFamily: fontFam,
            fontSize: "16px",
            lineHeight: "150%",
            letterSpacing: "0.15px",
            textAlign: "center",
            color: "rgba(0,0,0,0.6)",
          }}
        >
          Securely link your business's bank accounts to automatically track
          income, expenses, and financial health in real-time.
        </Typography>
      </Box>

      <Box sx={{ width: "100%", display: "flex", flexDirection: "column", gap: "20px" }}>
        <Typography
          sx={{
            fontFamily: fontFam,
            fontWeight: 600,
            fontSize: "1rem",
            lineHeight: "175%",
            letterSpacing: "0.15px",
            textAlign: "center",
          }}
        >
          We use industry-leading security measures to keep your information safe:
        </Typography>

      <Box
        display="grid"
        gridTemplateColumns={{
          xs: "1fr",
          sm: "repeat(2, 1fr)",
          md: "repeat(4, 1fr)",
        }}
        gap={"16px"}
        alignItems="stretch"
        sx={{ "& > *": { minHeight: 0 } }}
      >
        {modes.map((mode, index) => (
          <Card
            key={index}
            elevation={0}
            sx={{
              width: "100%",
              height: "100%",
              minHeight: "180px",
              borderRadius: "8px",
              border: "1px solid rgba(18, 22, 33, 0.08)",
              p: 2,
              display: "flex",
              flexDirection: "column",
            }}
          >
            <Box
              display="flex"
              flexDirection="column"
              alignItems="center"
              justifyContent="flex-start"
              flex={1}
              gap={1}
              textAlign="center"
            >
              <Box
                component="img"
                src={mode.icon}
                alt={`${mode.title} icon`}
                sx={{
                  width: "48px",
                  height: "48px",
                }}
              />

              <Typography
                variant="h6"
                fontWeight={600}
                sx={{
                  fontFamily: fontFam,
                  fontWeight: "600",
                  fontSize: { xs: "0.9rem", sm: "1rem" },
                  lineHeight: "175%",
                  letterSpacing: "0.15px",
                  textAlign: "center",
                  wordBreak: "break-word",
                  overflowWrap: "break-word",
                }}
              >
                {mode.title}
              </Typography>
              <Typography
                variant="body2"
                color="text.secondary"
                textAlign="center"
                sx={{
                  fontFamily: fontFam,
                  fontSize: "13px",
                  lineHeight: "143%",
                  letterSpacing: "0.17px",
                  wordBreak: "break-word",
                  overflowWrap: "break-word",
                }}
              >
                {mode.description}
              </Typography>
            </Box>
          </Card>
        ))}
      </Box>
    </Box>
    </Box>
  );
};


export const BankAccountSetupSkeleton = ({
  fontFam = "Rethink Sans",
  rows = 3,
  hideAccountSetupHeader = false,
} = {}) => (
  <Box sx={{ width: "100%" }}>
    {!hideAccountSetupHeader && (
      <>
        {/* Header */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            p: "16px",
            borderBottom: "1px solid rgba(0,0,0,0.08)",
          }}
        >
          <Box>
            <Skeleton variant="text" width={160} height={32} />
            <Skeleton
              variant="text"
              width={220}
              height={20}
              sx={{ mt: "2px" }}
            />
          </Box>
          <Skeleton
            variant="rounded"
            width={100}
            height={26}
            sx={{ borderRadius: "16px" }}
          />
        </Box>
      </>
    )}

    {/* Section header */}
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        px: "28px",
        pt: "24px",
        pb: "12px",
      }}
    >
      <Skeleton variant="text" width={160} height={28} />
      <Skeleton variant="rounded" width={110} height={32} sx={{ borderRadius: "6px" }} />
    </Box>

    {/* Table skeleton */}
    <Box sx={{ px: { xs: "12px", md: "20px" }, pb: "24px" }}>
      <Box sx={{ border: "1px solid rgba(0,0,0,0.08)", borderRadius: "12px", overflow: "hidden" }}>
        {/* Header row */}
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "56px 200px 240px 180px 1fr",
            bgcolor: "#EEF0FD",
            px: 1,
            py: 0.75,
            gap: 2,
          }}
        >
          {[0, 120, 110, 90, 100].map((w, i) => (
            <Skeleton key={i} variant="text" width={w || 0} height={20} sx={{ opacity: w ? 1 : 0 }} />
          ))}
        </Box>

        {/* Data rows */}
        {Array.from({ length: rows }).map((_, i) => (
          <Box
            key={i}
            sx={{
              display: "grid",
              gridTemplateColumns: "56px 200px 240px 180px 1fr",
              alignItems: "center",
              px: 1,
              py: 1,
              gap: 2,
              borderTop: "1px solid rgba(0,0,0,0.06)",
              bgcolor: i % 2 === 0 ? "#fff" : "rgba(0,0,0,0.01)",
            }}
          >
            <Skeleton variant="circular" width={28} height={28} sx={{ mx: "auto" }} />
            <Skeleton variant="rounded" width="80%" height={20} sx={{ borderRadius: "4px" }} />
            <Skeleton variant="rounded" width="75%" height={20} sx={{ borderRadius: "4px" }} />
            <Skeleton variant="rounded" width="60%" height={20} sx={{ borderRadius: "4px" }} />
            <Skeleton variant="rounded" width={90} height={28} sx={{ borderRadius: "6px" }} />
          </Box>
        ))}
      </Box>
    </Box>
  </Box>
);

export const BankAccountSetup = ({
  fontFam = "Rethink Sans",
  rows = [],
  setRows,
  onConnectData,
  onDeleteRow,
  onDisconnectRow,
  renderConnectedRowExtras,
  hideAccountSetupHeader = false,
} = {}) => {
  const tooltipProps = {
    placement: "top",
    arrow: true,
    disableFocusListener: true,
    disableTouchListener: true,
    disableInteractive: true,
  };
  const [rowErrors, setRowErrors] = useState({});
  const [connectMenuAnchorEl, setConnectMenuAnchorEl] = useState(null);
  const [connectMenuRow, setConnectMenuRow] = useState(null);
  const [editingRowId, setEditingRowId] = useState(null);
  const [rowDrafts, setRowDrafts] = useState({});
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [deleteTargetRow, setDeleteTargetRow] = useState(null);
  const [disconnectDialogOpen, setDisconnectDialogOpen] = useState(false);
  const [disconnectTargetRow, setDisconnectTargetRow] = useState(null);

  const connectedCount = (Array.isArray(rows) ? rows : []).filter(
    (r) => r?.isConnected
  ).length;
  const totalCount = Array.isArray(rows) ? rows.length : 0;

  const typeOptions = [
    { key: "checking", label: "Checking Account" },
    { key: "savings", label: "Savings Account" },
    { key: "credit_card", label: "Credit Account" },
  ];

  const derivePlaceholder = (typeKey, idx) => {
    if (typeKey === "checking") return `e.g. Checking ${idx}`;
    if (typeKey === "savings") return `e.g. Savings ${idx}`;
    if (typeKey === "credit_card") return `e.g. Credit ${idx}`;
    return "e.g. Account";
  };

  const handleAddRow = () => {
    if (!setRows) return;

    const checkingCount = (Array.isArray(rows) ? rows : []).filter(
      (r) => r?.typeKey === "checking"
    ).length;
    const nextIdx = checkingCount + 1;

    setRows((prev) => [
      ...(Array.isArray(prev) ? prev : []),
      {
        id: buildDraftRowId("checking", nextIdx),
        bankName: "",
        typeKey: "checking",
        accountName: "",
        isConnected: false,
        connectionMethod: null,
      },
    ]);
  };

  const handleDeleteRow = (rowId) => {
    if (!setRows) return;
    setRows((prev) =>
      (Array.isArray(prev) ? prev : []).filter((r) => r?.id !== rowId)
    );
    setRowErrors((prev) => {
      const next = { ...(prev || {}) };
      delete next[rowId];
      return next;
    });
  };

  const closeConnectMenu = () => {
    setConnectMenuAnchorEl(null);
    setConnectMenuRow(null);
  };

  const openDeleteConfirm = (row) => {
    setDeleteTargetRow(row || null);
    setDeleteConfirmOpen(true);
  };

  const closeDeleteConfirm = () => {
    setDeleteConfirmOpen(false);
    setDeleteTargetRow(null);
  };

  const confirmDeleteRow = () => {
    const row = deleteTargetRow;
    closeDeleteConfirm();

    if (onDeleteRow) {
      onDeleteRow(row);
      return;
    }

    const rowId = row?.id || null;
    if (!rowId) return;
    handleDeleteRow(rowId);
  };

  const startEditingRow = (row) => {
    const rowId = row?.id || null;
    if (!rowId) return;
    setRowDrafts((prev) => ({
      ...(prev || {}),
      [rowId]: {
        bankName: row?.bankName || "",
        accountName: row?.accountName || "",
        typeKey: row?.typeKey || "checking",
      },
    }));
    setEditingRowId(rowId);
  };

  const cancelEditingRow = () => {
    setEditingRowId(null);
  };

  const saveEditingRow = () => {
    if (!setRows) return;
    if (!editingRowId) return;
    const draft = rowDrafts?.[editingRowId];
    if (!draft) {
      setEditingRowId(null);
      return;
    }

    setRows((prev) =>
      (Array.isArray(prev) ? prev : []).map((r) =>
        r?.id === editingRowId
          ? {
              ...r,
              bankName: draft.bankName,
              accountName: draft.accountName,
              typeKey: draft.typeKey,
            }
          : r
      )
    );
    setEditingRowId(null);
  };

  const openDisconnectDialog = (row) => {
    setDisconnectTargetRow(row || null);
    setDisconnectDialogOpen(true);
  };

  const closeDisconnectDialog = () => {
    setDisconnectDialogOpen(false);
    setDisconnectTargetRow(null);
  };

  const confirmDisconnect = () => {
    const rowId = disconnectTargetRow?.id || null;
    if (!rowId) {
      closeDisconnectDialog();
      return;
    }

    onDisconnectRow?.(disconnectTargetRow);
    setEditingRowId(null);
    closeDisconnectDialog();
  };

  return (
    <Box sx={{ width: "100%" }}>
      {!hideAccountSetupHeader && (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            p: "16px",
            borderBottom: "1px solid rgba(0,0,0,0.08)",
          }}
        >
          <Box>
            <Typography
              sx={{
                fontFamily: fontFam,
                fontWeight: 600,
                fontSize: "24px",
                lineHeight: "133%",
                color: "rgba(0,0,0,0.87)",
              }}
            >
              Account Setup
            </Typography>
            <Typography
              sx={{
                fontFamily: fontFam,
                fontWeight: 400,
                fontSize: "14px",
                lineHeight: "143%",
                letterSpacing: "0.17px",
                color: "rgba(0,0,0,0.6)",
                mt: "2px",
              }}
            >
              Define your accounts and connect them.
            </Typography>
          </Box>

          <Chip
            label={`${connectedCount}/${totalCount} Connected`}
            sx={{
              backgroundColor: "rgba(0,0,0,0.08)",
              fontFamily: fontFam,
              fontWeight: 500,
              fontSize: "13px",
              letterSpacing: "0.16px",
              px: "4px",
            }}
          />
        </Box>
      )}

      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: "12px",
          py: "24px",
          width: "100%",
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            px: "28px",
          }}
        >
          <Typography
            sx={{
              fontFamily: fontFam,
              fontWeight: 600,
              fontSize: "1rem",
              lineHeight: "175%",
              letterSpacing: "0.15px",
              color: "rgba(0,0,0,0.87)",
            }}
          >
            Connected Accounts
          </Typography>

          <Button
            onClick={handleAddRow}
            startIcon={<AddIcon sx={{ fontSize: 18 }} />}
            variant="text"
            sx={{
              fontFamily: fontFam,
              textTransform: "none",
              fontWeight: 500,
              color: "#536DEC",
              px: "8px",
              py: "6px",
              minWidth: "auto",
              "& .MuiButton-startIcon": {
                mr: "6px",
              },
            }}
          >
            Add Account
          </Button>
        </Box>

        <Box sx={{ px: { xs: "12px", md: "20px" } }}>
          <Box
            sx={{
              border: "1px solid rgba(0,0,0,0.08)",
              borderRadius: "12px",
              overflow: "hidden",
            }}
          >
            <Box
              sx={{
                maxHeight: "clamp(280px, 42vh, 420px)",
                overflowY: "auto",
                overflowX: { xs: "auto", md: "hidden" },
              }}
            >
            <Table size="small" stickyHeader sx={{ tableLayout: "fixed" }}>
              <TableHead>
                <TableRow>
                  <TableCell
                    sx={{
                      fontFamily: fontFam,
                      fontWeight: 500,
                      backgroundColor: "#EEF0FD",
                      width: 56,
                      py: 0.75,
                      borderTopLeftRadius: "12px",
                    }}
                  />
                <TableCell
                  sx={{
                    fontFamily: fontFam,
                    fontWeight: 500,
                    backgroundColor: "#EEF0FD",
                    width: 200,
                    py: 0.75,
                    fontSize: "15px",
                    letterSpacing: "0.17px",
                    textTransform: "capitalize",
                  }}
                >
                  Bank Name
                </TableCell>
                  <TableCell
                    sx={{
                      fontFamily: fontFam,
                      fontWeight: 500,
                      backgroundColor: "#EEF0FD",
                      width: 240,
                      py: 0.75,
                      fontSize: "15px",
                      letterSpacing: "0.17px",
                      textTransform: "capitalize",
                    }}
                  >
                    Account Name
                  </TableCell>
                  <TableCell
                    sx={{
                      fontFamily: fontFam,
                      fontWeight: 500,
                      backgroundColor: "#EEF0FD",
                      width: 180,
                      py: 0.75,
                      fontSize: "15px",
                      letterSpacing: "0.17px",
                      textTransform: "capitalize",
                    }}
                  >
                    Type
                  </TableCell>
                  <TableCell
                    sx={{
                      fontFamily: fontFam,
                      fontWeight: 500,
                      backgroundColor: "#EEF0FD",
                      py: 0.75,
                      fontSize: "15px",
                      letterSpacing: "0.17px",
                      textTransform: "capitalize",
                      borderTopRightRadius: "12px",
                    }}
                  >
                    Connection
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(Array.isArray(rows) ? rows : []).map((row, idx) => {
                  const rowId = row?.id;
                const rowError = rowErrors?.[rowId] || null;
                const rowHasError = Boolean(rowError?.message);
                  const isEditing = Boolean(editingRowId && editingRowId === rowId);
                  const isConnected = Boolean(row?.isConnected);
                  const typeLabel =
                    typeOptions.find((t) => t.key === (row?.typeKey || "checking"))?.label ||
                    "Account";
                  const draft = rowDrafts?.[rowId] || {};
                const bankNameError = rowError?.field === "bankName";
                const accountNameError = rowError?.field === "accountName";

                  return (
                  <TableRow
                    key={rowId || idx}
                    sx={{
                      backgroundColor: rowHasError
                        ? "#FFFBFB"
                        : isEditing
                        ? "rgba(83,109,236,0.03)"
                        : "transparent",
                      "& > .MuiTableCell-root": {
                        verticalAlign: rowHasError ? "top" : "middle",
                      },
                    }}
                  >
                    <TableCell sx={{ py: 1, pl: "7px", pr: 0 }}>
                      {isConnected && !isEditing ? (
                        <Box sx={{ width: 34, height: 34 }} />
                      ) : (
                        <Tooltip title="Delete" {...tooltipProps}>
                          <IconButton
                            onMouseDown={(event) => {
                              event.preventDefault();
                              event.stopPropagation();
                            }}
                            onClick={(event) => {
                              event.preventDefault();
                              event.stopPropagation();
                              openDeleteConfirm(row);
                            }}
                            sx={{
                              p: "5px",
                              borderRadius: "100px",
                              "&:hover": {
                                backgroundColor: isEditing
                                  ? "rgba(211,47,47,0.04)"
                                  : "rgba(0,0,0,0.04)",
                              },
                            }}
                          >
                            <Trash2
                              size={20}
                              color={isEditing ? "#D32F2F" : "rgba(0,0,0,0.6)"}
                            />
                          </IconButton>
                        </Tooltip>
                      )}
                    </TableCell>

                  <TableCell sx={{ py: 1 }}>
                    {isConnected && !isEditing ? (
                      <Box sx={{ overflow: "hidden" }}>
                        <Tooltip title={row?.bankName || ""} placement="top" disableHoverListener={!row?.bankName}>
                          <Typography
                            sx={{
                              fontFamily: fontFam,
                              fontSize: "14px",
                              letterSpacing: "0.17px",
                              color: "rgba(0,0,0,0.87)",
                              display: "-webkit-box",
                              WebkitLineClamp: 2,
                              WebkitBoxOrient: "vertical",
                              overflow: "hidden",
                              cursor: "default",
                            }}
                          >
                            {row?.bankName || ""}
                          </Typography>
                        </Tooltip>
                      </Box>
                    ) : (
                      <TextField
                        fullWidth
                        size="small"
                        value={
                          isEditing ? (draft.bankName ?? "") : (row?.bankName || "")
                        }
                        onChange={(e) => {
                          const next = e.target.value;
                          const trimmed = next.trim();
                          if (trimmed) {
                            setRowErrors((prev) => {
                              if (prev?.[row?.id]?.field !== "bankName") return prev;
                              const copy = { ...(prev || {}) };
                              delete copy[row?.id];
                              return copy;
                            });
                          }

                          if (isEditing) {
                            setRowDrafts((prev) => ({
                              ...(prev || {}),
                              [rowId]: { ...(prev?.[rowId] || {}), bankName: next },
                            }));
                            return;
                          }

                          if (!setRows) return;
                          setRows((prev) =>
                            (Array.isArray(prev) ? prev : []).map((r) =>
                              r?.id === row?.id ? { ...r, bankName: next } : r
                            )
                          );
                        }}
                        placeholder="e.g. Chase Bank"
                        error={bankNameError}
                        helperText={
                          bankNameError ? (
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: "4px",
                              }}
                            >
                              <ErrorOutlineIcon sx={{ fontSize: 16, color: "#D32F2F" }} />
                              <Typography
                                sx={{
                                  fontFamily: fontFam,
                                  fontWeight: 400,
                                  fontSize: "12px",
                                  lineHeight: "143%",
                                  letterSpacing: "0.17px",
                                  color: "#D32F2F",
                                }}
                              >
                                {rowError?.message}
                              </Typography>
                            </Box>
                          ) : undefined
                        }
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            borderRadius: "4px",
                            backgroundColor: "#fff",
                            "& .MuiOutlinedInput-notchedOutline": {
                              borderColor: "#E0E0E0",
                            },
                            "&:hover .MuiOutlinedInput-notchedOutline": {
                              borderColor: "#E0E0E0",
                            },
                            "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                              borderColor: "#536DEC",
                            },
                            "&.Mui-error .MuiOutlinedInput-notchedOutline": {
                              borderColor: "#D32F2F",
                            },
                          },
                          "& .MuiOutlinedInput-input": {
                            px: "11px",
                            py: "8px",
                            fontFamily: fontFam,
                            fontSize: "14px",
                            letterSpacing: "0.17px",
                          },
                          "& .MuiFormHelperText-root": {
                            mx: 0,
                            mt: "4px",
                          },
                        }}
                      />
                    )}
                  </TableCell>

                    <TableCell sx={{ py: 1 }}>
                      {isConnected && !isEditing ? (
                        <Box sx={{ overflow: "hidden" }}>
                          <Tooltip title={row?.accountName || ""} placement="top" disableHoverListener={!row?.accountName}>
                            <Typography
                              sx={{
                                fontFamily: fontFam,
                                fontSize: "14px",
                                letterSpacing: "0.17px",
                                color: "rgba(0,0,0,0.87)",
                                display: "-webkit-box",
                                WebkitLineClamp: 2,
                                WebkitBoxOrient: "vertical",
                                overflow: "hidden",
                                cursor: "default",
                              }}
                            >
                              {row?.accountName || ""}
                            </Typography>
                          </Tooltip>
                        </Box>
                      ) : (
                        <TextField
                          fullWidth
                          size="small"
                          value={isEditing ? (draft.accountName ?? "") : (row?.accountName || "")}
                          onChange={(e) => {
                            const next = e.target.value;
                            const trimmed = next.trim();
                            if (trimmed) {
                              setRowErrors((prev) => {
                              if (prev?.[row?.id]?.field !== "accountName") return prev;
                                const copy = { ...(prev || {}) };
                                delete copy[row?.id];
                                return copy;
                              });
                            }

                            if (isEditing) {
                              setRowDrafts((prev) => ({
                                ...(prev || {}),
                                [rowId]: { ...(prev?.[rowId] || {}), accountName: next },
                              }));
                              return;
                            }

                            if (!setRows) return;
                            setRows((prev) =>
                              (Array.isArray(prev) ? prev : []).map((r) =>
                                r?.id === row?.id ? { ...r, accountName: next } : r
                              )
                            );
                          }}
                          placeholder={derivePlaceholder(row?.typeKey, idx + 1)}
                          error={accountNameError}
                          helperText={
                            accountNameError ? (
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: "4px",
                                }}
                              >
                                <ErrorOutlineIcon sx={{ fontSize: 16, color: "#D32F2F" }} />
                                <Typography
                                  sx={{
                                    fontFamily: fontFam,
                                    fontWeight: 400,
                                    fontSize: "12px",
                                    lineHeight: "143%",
                                    letterSpacing: "0.17px",
                                    color: "#D32F2F",
                                  }}
                                >
                                  {rowError?.message}
                                </Typography>
                              </Box>
                            ) : undefined
                          }
                          sx={{
                            "& .MuiOutlinedInput-root": {
                              borderRadius: "4px",
                              backgroundColor: "#fff",
                              "& .MuiOutlinedInput-notchedOutline": {
                                borderColor: "#E0E0E0",
                              },
                              "&:hover .MuiOutlinedInput-notchedOutline": {
                                borderColor: "#E0E0E0",
                              },
                              "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                                borderColor: "#536DEC",
                              },
                              "&.Mui-error .MuiOutlinedInput-notchedOutline": {
                                borderColor: "#D32F2F",
                              },
                            },
                            "& .MuiOutlinedInput-input": {
                              px: "11px",
                              py: "8px",
                              fontFamily: fontFam,
                              fontSize: "14px",
                              letterSpacing: "0.17px",
                            },
                            "& .MuiFormHelperText-root": {
                              mx: 0,
                              mt: "4px",
                            },
                          }}
                        />
                      )}
                    </TableCell>

                    <TableCell sx={{ py: 1 }}>
                      {isConnected && !isEditing ? (
                        <Typography
                          sx={{
                            fontFamily: fontFam,
                            fontSize: "14px",
                            letterSpacing: "0.17px",
                            color: "rgba(0,0,0,0.87)",
                          }}
                        >
                          {typeLabel}
                        </Typography>
                      ) : (
                        <TextField
                          select
                          fullWidth
                          size="small"
                          value={
                            isEditing ? (draft.typeKey ?? (row?.typeKey || "checking")) : (row?.typeKey || "checking")
                          }
                          SelectProps={{
                            MenuProps: {disablePortal: true,
                            },
                          }}
                          onChange={(e) => {
                            const next = e.target.value;

                            if (isEditing) {
                              setRowDrafts((prev) => ({
                                ...(prev || {}),
                                [rowId]: { ...(prev?.[rowId] || {}), typeKey: next },
                              }));
                              return;
                            }

                            if (!setRows) return;
                            setRows((prev) =>
                              (Array.isArray(prev) ? prev : []).map((r) =>
                                r?.id === row?.id ? { ...r, typeKey: next } : r
                              )
                            );
                          }}
                          sx={{
                            "& .MuiOutlinedInput-root": {
                              borderRadius: "4px",
                              backgroundColor: "#fff",
                              "& .MuiOutlinedInput-notchedOutline": {
                                borderColor: "#E0E0E0",
                              },
                              "&:hover .MuiOutlinedInput-notchedOutline": {
                                borderColor: "#E0E0E0",
                              },
                              "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                                borderColor: "#536DEC",
                              },
                            },
                            "& .MuiOutlinedInput-input": {
                              px: "11px",
                              py: "8px",
                              fontFamily: fontFam,
                              fontSize: "14px",
                              letterSpacing: "0.17px",
                            },
                          }}
                        >
                          {typeOptions.map((opt) => (
                            <MenuItem key={opt.key} value={opt.key}>
                              {opt.label}
                            </MenuItem>
                          ))}
                        </TextField>
                      )}
                    </TableCell>

                    <TableCell sx={{ py: 1 }}>
                      {isEditing ? (
                        <Box sx={{ display: "flex", alignItems: "center", gap: "12px" }}>
                          <Tooltip title="Remove Connection" {...tooltipProps}>
                            <IconButton
                              size="small"
                              onMouseDown={(event) => {
                                event.preventDefault();
                                event.stopPropagation();
                              }}
                              onClick={(event) => {
                                event.preventDefault();
                                event.stopPropagation();
                                openDisconnectDialog(row);
                              }}
                              sx={{
                                p: "4px",
                                borderRadius: "4px",
                                "&:hover": { backgroundColor: "rgba(0,0,0,0.04)" },
                              }}
                            >
                              <LinkOffOutlinedIcon sx={{ fontSize: 18, color: "rgba(0,0,0,0.54)" }} />
                            </IconButton>
                          </Tooltip>

                          <Tooltip title="Cancel" {...tooltipProps}>
                            <IconButton
                              size="small"
                              onMouseDown={(event) => {
                                event.preventDefault();
                                event.stopPropagation();
                              }}
                              onClick={(event) => {
                                event.preventDefault();
                                event.stopPropagation();
                                cancelEditingRow();
                              }}
                              sx={{
                                p: "4px",
                                borderRadius: "4px",
                                "&:hover": { backgroundColor: "rgba(211,47,47,0.06)" },
                              }}
                            >
                              <CloseOutlinedIcon sx={{ fontSize: 18, color: "#D32F2F" }} />
                            </IconButton>
                          </Tooltip>

                          <Tooltip title="Confirm" {...tooltipProps}>
                            <IconButton
                              size="small"
                              onMouseDown={(event) => {
                                event.preventDefault();
                                event.stopPropagation();
                              }}
                              onClick={(event) => {
                                event.preventDefault();
                                event.stopPropagation();
                                saveEditingRow();
                              }}
                              sx={{
                                p: "4px",
                                borderRadius: "4px",
                                "&:hover": { backgroundColor: "rgba(46,125,50,0.08)" },
                              }}
                            >
                              <CheckOutlinedIcon sx={{ fontSize: 18, color: "#2E7D32" }} />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      ) : isConnected ? (
                        <Box sx={{ display: "flex", alignItems: "center", gap: "12px" }}>
                          {row?.connectionMethod === "csv" ? (
                            <Chip
                              label={
                                <Box sx={{ display: "flex", alignItems: "center", gap: "6px" }}>
                                  <Typography
                                    component="span"
                                    sx={{
                                      fontFamily: fontFam,
                                      fontWeight: 500,
                                      fontSize: "0.8125rem",
                                      lineHeight: "18px",
                                      letterSpacing: "0.16px",
                                    }}
                                  >
                                    CSV
                                  </Typography>
                                  <DescriptionOutlinedIcon sx={{ fontSize: 16 }} />
                                </Box>
                              }
                              size="small"
                              sx={{
                                fontFamily: fontFam,
                                backgroundColor: "#E5F6FD",
                                color: "#0288D1",
                                "& .MuiChip-label": { px: "6px" },
                                "& .MuiSvgIcon-root": { color: "#0288D1" },
                              }}
                            />
                          ) : (
                            <Chip
                              label={
                                <Box sx={{ display: "flex", alignItems: "center", gap: "6px" }}>
                                  <Typography
                                    component="span"
                                    sx={{
                                      fontFamily: fontFam,
                                      fontWeight: 500,
                                      fontSize: "0.8125rem",
                                      lineHeight: "18px",
                                      letterSpacing: "0.16px",
                                    }}
                                  >
                                    Plaid
                                  </Typography>
                                  <LinkOutlinedIcon sx={{ fontSize: 16 }} />
                                </Box>
                              }
                              size="small"
                              sx={{
                                fontFamily: fontFam,
                                backgroundColor: "rgba(46, 125, 50, 0.08)",
                                color: "#2E7D32",
                                "& .MuiChip-label": { px: "6px" },
                                "& .MuiSvgIcon-root": { color: "#2E7D32" },
                              }}
                            />
                          )}

                          {typeof renderConnectedRowExtras === "function"
                            ? renderConnectedRowExtras(row)
                            : null}

                          <Tooltip title="Edit" {...tooltipProps}>
                            <IconButton
                              size="small"
                              onMouseDown={(event) => {
                                event.preventDefault();
                                event.stopPropagation();
                              }}
                              onClick={(event) => {
                                event.preventDefault();
                                event.stopPropagation();
                                event.currentTarget.blur();
                                startEditingRow(row);
                              }}
                              sx={{
                                p: "4px",
                                borderRadius: "4px",
                                "&:hover": { backgroundColor: "rgba(0,0,0,0.04)" },
                              }}
                            >
                              <EditOutlinedIcon
                                sx={{ fontSize: 16, color: "rgba(0,0,0,0.54)" }}
                              />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      ) : (
                        <Button
                          variant="text"
                          onClick={(event) => {
                            event.preventDefault();
                            event.stopPropagation();
                            const bankName = (row?.bankName || "").trim();
                            const accountName = (row?.accountName || "").trim();
                            if (!bankName) {
                              setRowErrors((prev) => ({
                                ...(prev || {}),
                                [row?.id]: {
                                  field: "bankName",
                                  message: "Bank name is required",
                                },
                              }));
                              return;
                            }
                            if (!accountName) {
                              setRowErrors((prev) => ({
                                ...(prev || {}),
                                [row?.id]: {
                                  field: "accountName",
                                  message: "Account name is required",
                                },
                              }));
                              return;
                            }

                            setConnectMenuAnchorEl(event.currentTarget);
                            setConnectMenuRow(row);
                          }}
                          sx={{
                            fontFamily: fontFam,
                            textTransform: "none",
                            fontWeight: 500,
                            color: "#536DEC",
                            px: 0,
                            minWidth: "auto",
                          }}
                        >
                          Connect Data
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                  );
                })}
              </TableBody>
            </Table>
            </Box>
          </Box>
        </Box>
      </Box>

      <Menu
        anchorEl={connectMenuAnchorEl}
        open={Boolean(connectMenuAnchorEl)}
        onClose={closeConnectMenu}
        onClick={(event) => event.stopPropagation()}
        disablePortal={true}
        onMouseDown={(event) => {
          event.preventDefault();
          event.stopPropagation();
        }}
        PaperProps={{
          sx: {
            borderRadius: "4px",
            py: "8px",
            minWidth: "180px",
          },
        }}
        MenuListProps={{
          sx: {
            py: 0,
          },
        }}
      >
        <MenuItem
          onMouseDown={(event) => {
            event.preventDefault();
            event.stopPropagation();
          }}
          onMouseUp={(event) => {
            event.preventDefault();
            event.stopPropagation();
          }}
          onClick={(event) => {
            event.preventDefault();
            event.stopPropagation();
            event.nativeEvent?.stopImmediatePropagation?.();
            const row = connectMenuRow;
            onConnectData?.(row, { method: "plaid" });
            closeConnectMenu();
          }}
          sx={{
            borderRadius: "8px",
            mx: "8px",
            px: "16px",
            py: "4px",
            gap: "10px",
          }}
        >
          <LinkOutlinedIcon sx={{ fontSize: 19.2, color: "rgba(0,0,0,0.87)" }} />
          <Typography
            sx={{
              fontFamily: fontFam,
              fontWeight: 400,
              fontSize: "14px",
              lineHeight: "24px",
              letterSpacing: "0.17px",
              color: "rgba(0,0,0,0.87)",
            }}
          >
            Link via Plaid
          </Typography>
        </MenuItem>

        <MenuItem
          onMouseDown={(event) => {
            event.preventDefault();
            event.stopPropagation();
          }}
          onMouseUp={(event) => {
            event.preventDefault();
            event.stopPropagation();
          }}
          onClick={(event) => {
            event.preventDefault();
            event.stopPropagation();
            event.nativeEvent?.stopImmediatePropagation?.();
            const row = connectMenuRow;
            onConnectData?.(row, { method: "csv" });
            closeConnectMenu();
          }}
          sx={{
            borderRadius: "8px",
            mx: "8px",
            px: "16px",
            py: "4px",
            gap: "10px",
          }}
        >
          <FileUploadOutlinedIcon
            sx={{ fontSize: 19.2, color: "rgba(0,0,0,0.87)" }}
          />
          <Typography
            sx={{
              fontFamily: fontFam,
              fontWeight: 400,
              fontSize: "14px",
              lineHeight: "24px",
              letterSpacing: "0.17px",
              color: "rgba(0,0,0,0.87)",
            }}
          >
            Upload CSV
          </Typography>
        </MenuItem>
      </Menu>

      <Dialog
        open={disconnectDialogOpen}
        onClose={closeDisconnectDialog}
        disablePortal={true}
        PaperProps={{
          sx: {
            borderRadius: "16px",
            overflow: "visible",
            maxWidth: "560px",
            width: "calc(100% - 48px)",
            boxShadow: "0px 2px 4px 0px rgba(0,0,0,0.08)",
          },
        }}
      >
        <DialogContent
          sx={{
            overflow: "visible",
            pt: "60px",
            pb: "24px",
            px: "20px",
            display: "flex",
            flexDirection: "column",
            gap: "32px",
            alignItems: "center",
            position: "relative",
            textAlign: "center",
          }}
        >
          <Box
            sx={{
              position: "absolute",
              top: "-40px",
              left: "50%",
              transform: "translateX(-50%)",
              width: "80px",
              height: "80px",
              borderRadius: "100px",
              backgroundColor: "#E53935",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <WarningAmberOutlinedIcon sx={{ color: "#FFFFFF", fontSize: 40 }} />
          </Box>

          <Box sx={{ width: "100%" }}>
            <Typography
              sx={{
                fontFamily: fontFam,
                fontWeight: 600,
                fontSize: "24px",
                lineHeight: "133%",
                color: "rgba(0,0,0,0.87)",
                mb: "8px",
              }}
            >
              Are you sure?
            </Typography>
            <Typography
              sx={{
                fontFamily: fontFam,
                fontWeight: 400,
                fontSize: "16px",
                lineHeight: "150%",
                letterSpacing: "0.15px",
                color: "rgba(0,0,0,0.6)",
              }}
            >
              Are you sure you want to remove the connection for{" "}
              <Box component="span" sx={{ fontFamily: fontFam, fontWeight: 600, color: "rgba(0,0,0,0.87)" }}>
                {disconnectTargetRow?.accountName || "this account"}
              </Box>
              ? The account details will be kept, but you'll need to reconnect it.
            </Typography>
          </Box>

          <Box sx={{ width: "100%", display: "flex", flexDirection: "column", gap: "16px" }}>
            <Button
              variant="contained"
              startIcon={<LinkOffOutlinedIcon />}
              onClick={confirmDisconnect}
              sx={{
                width: "100%",
                borderRadius: "8px",
                px: "22px",
                py: "8px",
                textTransform: "none",
                fontFamily: fontFam,
                fontWeight: 500,
                fontSize: "15px",
                letterSpacing: "0.46px",
                backgroundColor: "#D32F2F",
                "&:hover": { backgroundColor: "#C62828" },
                boxShadow:
                  "0px 1px 5px 0px rgba(0,0,0,0.12), 0px 2px 2px 0px rgba(0,0,0,0.14), 0px 3px 1px -2px rgba(0,0,0,0.2)",
              }}
            >
              Remove Connection
            </Button>
            <Button
              variant="outlined"
              onClick={closeDisconnectDialog}
              sx={{
                width: "100%",
                borderRadius: "8px",
                px: "22px",
                py: "8px",
                textTransform: "none",
                fontFamily: fontFam,
                fontWeight: 500,
                fontSize: "15px",
                letterSpacing: "0.46px",
                borderColor: "rgba(0,0,0,0.87)",
                color: "rgba(0,0,0,0.87)",
                "&:hover": { borderColor: "rgba(0,0,0,0.87)" },
              }}
            >
              Cancel
            </Button>
          </Box>
        </DialogContent>
      </Dialog>

      <Dialog
        open={deleteConfirmOpen}
        onClose={closeDeleteConfirm}
        disablePortal={true}
        PaperProps={{
          sx: {
            borderRadius: "16px",
            overflow: "visible",
            maxWidth: "560px",
            width: "calc(100% - 48px)",
            boxShadow: "0px 2px 4px 0px rgba(0,0,0,0.08)",
          },
        }}
      >
        <DialogContent
          sx={{
            overflow: "visible",
            pt: "60px",
            pb: "24px",
            px: "20px",
            display: "flex",
            flexDirection: "column",
            gap: "32px",
            alignItems: "center",
            position: "relative",
            textAlign: "center",
          }}
        >
          <Box
            sx={{
              position: "absolute",
              top: "-40px",
              left: "50%",
              transform: "translateX(-50%)",
              width: "80px",
              height: "80px",
              borderRadius: "100px",
              backgroundColor: "#E53935",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Trash2 size={40} color="#FFFFFF" />
          </Box>

          <Box sx={{ width: "100%" }}>
            <Typography
              sx={{
                fontFamily: fontFam,
                fontWeight: 600,
                fontSize: "24px",
                lineHeight: "133%",
                color: "rgba(0,0,0,0.87)",
                mb: "8px",
              }}
            >
              Are you sure?
            </Typography>
            <Typography
              sx={{
                fontFamily: fontFam,
                fontWeight: 400,
                fontSize: "16px",
                lineHeight: "150%",
                letterSpacing: "0.15px",
                color: "rgba(0,0,0,0.6)",
              }}
            >
              Are you sure you want to delete{" "}
              <Box component="span" sx={{ fontFamily: fontFam, fontWeight: 600, color: "rgba(0,0,0,0.87)" }}>
                {deleteTargetRow?.accountName || "this account"}
              </Box>
              ? You can add it again later if needed.
            </Typography>
          </Box>

          <Box sx={{ width: "100%", display: "flex", flexDirection: "column", gap: "16px" }}>
            <Button
              variant="contained"
              startIcon={<Trash2 size={18} />}
              onClick={confirmDeleteRow}
              sx={{
                width: "100%",
                borderRadius: "8px",
                px: "22px",
                py: "8px",
                textTransform: "none",
                fontFamily: fontFam,
                fontWeight: 500,
                fontSize: "15px",
                letterSpacing: "0.46px",
                backgroundColor: "#D32F2F",
                "&:hover": { backgroundColor: "#C62828" },
                boxShadow:
                  "0px 1px 5px 0px rgba(0,0,0,0.12), 0px 2px 2px 0px rgba(0,0,0,0.14), 0px 3px 1px -2px rgba(0,0,0,0.2)",
              }}
            >
              Delete Account
            </Button>
            <Button
              variant="outlined"
              onClick={closeDeleteConfirm}
              sx={{
                width: "100%",
                borderRadius: "8px",
                px: "22px",
                py: "8px",
                textTransform: "none",
                fontFamily: fontFam,
                fontWeight: 500,
                fontSize: "15px",
                letterSpacing: "0.46px",
                borderColor: "rgba(0,0,0,0.87)",
                color: "rgba(0,0,0,0.87)",
                "&:hover": { borderColor: "rgba(0,0,0,0.87)" },
              }}
            >
              Cancel
            </Button>
          </Box>
        </DialogContent>
      </Dialog>
    </Box>
  );
};
